import type { Sample } from '@ctcells/contract'

/**
 * 시료 정정 본문 만들기 — 화면과 갈라 둔 것은 **시험이 이 계산만 따로 잴 수 있게** 하려는 것이다
 * (`sampleModel.ts` 와 같은 자리).
 */

/**
 * 정정 본문의 값 부분 — `CorrectSampleBody` 에서 `command_id`·`reason`·`metadata` 를 뺀 것.
 *
 * 🔴 **`null` 을 담을 수 있어야 한다.** 값을 «지우는» 정정이 있고, 계약이 그 칸들을 전부
 *    nullable 로 선언했다. `Partial<Sample>` 로는 표현할 수 없다(생성 타입이 `null` 을 안 받는다).
 * ⚠ `type` 이지 `interface` 가 아니다 — `Record<string, unknown>`(명령 훅의 `patch`)에
 *   넘기려면 암묵 인덱스 서명이 필요하다.
 */
export type SampleCorrection = {
  patient_id?: string | null
  sample_type?: string | null
  rbc_concentration?: number | null
  blood_draw_date?: string | null
  cancer_type?: string | null
  cancer_stage?: string | null
}

/** 화면이 쥐는 문자열 초안. 키는 계약 본문 키 그대로다 — 옮기는 자리를 만들지 않는다 */
export type CorrectionDraft = Record<keyof SampleCorrection, string>

const textOf = (value: string | number | null | undefined): string =>
  value == null ? '' : String(value)

export function draftOf(sample: Sample): CorrectionDraft {
  return {
    patient_id: textOf(sample.patient_id),
    sample_type: textOf(sample.sample_type),
    rbc_concentration: textOf(sample.rbc_concentration),
    blood_draw_date: textOf(sample.blood_draw_date),
    cancer_type: textOf(sample.cancer_type),
    cancer_stage: textOf(sample.cancer_stage),
  }
}

/**
 * 초안 ↔ 원본을 대조해 **바뀐 칸만** 본문으로 만든다.
 *
 * - 비운 칸은 `null` 이다 — 「지워 달라」는 정정이고 서버가 그 뜻으로 받는다.
 * - 적혈구 농도만 숫자다. **숫자가 아니면 그 칸을 보내지 않는다** — `0` 으로 바꾸면 없던 값이 생긴다.
 * - 🔴 `patient_id` 는 **가려져 있으면 아예 빠진다.** 마스크 문자열을 값으로 올려보내지 않는다.
 */
export function correctionOf(
  sample: Sample,
  draft: CorrectionDraft,
  options: { patientIdEditable: boolean },
): SampleCorrection {
  const base = draftOf(sample)
  const patch: SampleCorrection = {}

  for (const key of Object.keys(base) as (keyof SampleCorrection)[]) {
    if (key === 'patient_id' && !options.patientIdEditable) continue
    const next = draft[key].trim()
    if (next === base[key].trim()) continue

    if (key === 'rbc_concentration') {
      if (next === '') {
        patch.rbc_concentration = null
        continue
      }
      const parsed = Number(next)
      if (!Number.isFinite(parsed)) continue
      patch.rbc_concentration = parsed
      continue
    }
    patch[key] = next === '' ? null : next
  }

  return patch
}
